import type { AxiosInstance } from "axios";

/** Mirrors apps/api's OnboardingStatus. */
export interface OnboardingStatus {
  readonly tenantId: string;
  readonly shopDomain: string;
  readonly state: string;
  readonly trackingVerified: boolean;
  readonly lastEventAt: string | null;
  readonly eventsSeen: number;
  readonly trackerVersion: string | null;
  readonly firstOrderTracked: boolean;
  readonly enableTrackingUrl: string | null;
  readonly nextStep: "enable-tracking" | "waiting-for-traffic" | "waiting-for-order" | "done";
}

export async function fetchOnboarding(client: AxiosInstance): Promise<OnboardingStatus> {
  const response = await client.get<{ success: boolean; data: OnboardingStatus }>("/onboarding/status");
  return response.data.data;
}

/**
 * The wizard's three steps, derived from `nextStep`. Each is done, current, or upcoming —
 * so a merchant sees where they are, not just what is left.
 *
 * The steps are FIXED and ordered; `nextStep` names the first not-yet-done one. Deriving
 * status from that keeps the server the single source of truth for "how far are they" and
 * the screen purely a rendering of it.
 */
export type StepStatus = "done" | "current" | "upcoming";

export interface WizardStep {
  readonly key: "enable-tracking" | "waiting-for-traffic" | "waiting-for-order";
  readonly title: string;
  readonly status: StepStatus;
}

const ORDER = ["enable-tracking", "waiting-for-traffic", "waiting-for-order"] as const;

export function wizardSteps(status: OnboardingStatus): readonly WizardStep[] {
  const titles: Record<(typeof ORDER)[number], string> = {
    "enable-tracking": "Turn on tracking",
    "waiting-for-traffic": "Confirm visits are arriving",
    "waiting-for-order": "Confirm an order is tracked",
  };

  // `done` means the whole wizard finished — every step is complete.
  const currentIndex = status.nextStep === "done" ? ORDER.length : ORDER.indexOf(status.nextStep);

  return ORDER.map((key, index) => ({
    key,
    title: titles[key],
    status: index < currentIndex ? "done" : index === currentIndex ? "current" : "upcoming",
  }));
}

export function isLive(status: OnboardingStatus): boolean {
  return status.nextStep === "done";
}
